// v3.15-1184 — Lead Warehouse operator page (Slice B).
//
// Read warehouse inventory + bulk-distribute (state / zip / source /
// batch / filters / selected rows) with single-rep or round-robin
// modes. Return-to-warehouse is a deliberate manager action — never
// automatic. Reuses Shadcn primitives + the existing api client; does
// NOT fork the assignment subsystem in any way.

import { useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Loader2, RefreshCw, Users as UsersIcon, Send, X,
  CheckCircle2, Undo2, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_TONES = {
  Imported:              'bg-slate-100 text-slate-700',
  Assigned:              'bg-indigo-100 text-indigo-800',
  Working:               'bg-amber-100 text-amber-800',
  Qualified:             'bg-emerald-100 text-emerald-800',
  'Application Started': 'bg-blue-100 text-blue-800',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

// ─────────────────────────────────────────────────────────────────
// Bulk Assign modal — v3.21-1792 · Cross-company search-first.
// Opens EMPTY; no Hub directory preload.  Operators type ≥2 chars to
// hit /lead-warehouse/employee-search which returns ≤20 eligible
// active users across ANY company.  Cross-company Warehouse Lead
// assignments are intentional per v1792 spec.  Assignment does not
// mutate the target user's company/role/hierarchy — enforced
// server-side by _resolve_targets + /assign.
// ─────────────────────────────────────────────────────────────────
const BulkAssignModal = ({ open, onClose, selector, selectedIds, onSuccess }) => {
  const [targetIds, setTargetIds] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState([]); // [{id, full_name, email, company_name}]
  const [mode, setMode]           = useState('round_robin');
  const [preview, setPreview]     = useState(null);
  const [busy, setBusy]           = useState(false);
  // v3.21-1792 · Server-side search state.  Empty results on open.
  const [repQuery, setRepQuery]   = useState('');
  const [reps, setReps]           = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (open) {
      setTargetIds([]); setSelectedTargets([]);
      setMode('round_robin'); setPreview(null);
      setRepQuery(''); setReps([]); setSearching(false);
    }
  }, [open]);

  // v3.21-1792 · Debounced server-side search.
  useEffect(() => {
    if (!open) return undefined;
    const q = (repQuery || '').trim();
    if (q.length < 2) { setReps([]); setSearching(false); return undefined; }
    setSearching(true);
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get('/lead-warehouse/employee-search', {
          params: { q, limit: 20 },
        });
        setReps(data?.items || []);
      } catch (e) {
        setReps([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(h);
  }, [repQuery, open]);

  if (!open) return null;

  const effectiveSelector = selectedIds.length
    ? { ids: selectedIds, only_unassigned: false }
    : selector;

  const toggleTarget = (rep) => {
    setTargetIds((arr) => arr.includes(rep.id) ? arr.filter((x) => x !== rep.id) : [...arr, rep.id]);
    setSelectedTargets((arr) => {
      if (arr.some((r) => r.id === rep.id)) return arr.filter((r) => r.id !== rep.id);
      return [...arr, rep];
    });
  };

  const runPreview = async () => {
    if (!targetIds.length) return toast.error('Pick at least one rep.');
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post('/lead-warehouse/assign-preview', {
        selector:   effectiveSelector,
        target_ids: targetIds,
        mode:       targetIds.length === 1 ? 'single' : mode,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const runAssign = async () => {
    if (!preview || preview.will_assign === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post('/lead-warehouse/assign', {
        selector:   effectiveSelector,
        target_ids: targetIds,
        mode:       targetIds.length === 1 ? 'single' : mode,
      });
      toast.success(`Assigned ${data.assigned} lead${data.assigned === 1 ? '' : 's'}.`);
      onSuccess?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assignment failed');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="bulk-assign-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[720px] max-w-[95vw] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-assign-modal"
      >
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="font-heading text-xl">Bulk Assign Warehouse Leads</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bulk-assign-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-5">
          <div className="bg-slate-50 border rounded p-3 text-sm" data-testid="bulk-assign-selector-summary">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Selection</p>
            {selectedIds.length ? (
              <p><strong>{selectedIds.length}</strong> rows selected in the table.</p>
            ) : (
              <ul className="text-slate-700 list-disc list-inside">
                {selector.state &&           <li>State = <strong>{selector.state}</strong></li>}
                {selector.zip_code &&        <li>ZIP = <strong>{selector.zip_code}</strong></li>}
                {selector.lead_source &&     <li>Lead source = <strong>{selector.lead_source}</strong></li>}
                {selector.import_batch_id && <li>Import batch = <strong>{selector.import_batch_id.slice(0,8)}…</strong></li>}
                {selector.source &&          <li>Intake source = <strong>{selector.source}</strong></li>}
                {selector.only_unassigned && <li>Only currently <strong>unassigned</strong> leads.</li>}
                {!selector.state && !selector.zip_code && !selector.lead_source && !selector.import_batch_id && !selector.source && (
                  <li className="text-slate-400 italic">No filters set — every unassigned warehouse lead will match.</li>
                )}
              </ul>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Target reps</p>
            {/* v3.21-1792 · Search-first: modal opens empty, results
                only appear once operators type ≥2 chars.  Cross-company
                users are eligible per v1792 spec. */}
            <Input
              value={repQuery}
              onChange={(e) => setRepQuery(e.target.value)}
              placeholder="Search employees by name or email..."
              className="h-9 text-sm mb-2"
              data-testid="employee-search-input"
              autoFocus
            />
            <div className="max-h-56 overflow-y-auto border rounded divide-y" data-testid="bulk-assign-rep-list">
              {selectedTargets.length > 0 && (
                <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-200" data-testid="bulk-assign-selected-summary">
                  <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-1">
                    Selected ({selectedTargets.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedTargets.map((r) => (
                      <span
                        key={r.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-indigo-300 text-[11px]"
                        data-testid={`bulk-assign-selected-${r.id}`}
                      >
                        {r.full_name || r.email}
                        <button
                          type="button"
                          onClick={() => toggleTarget(r)}
                          className="text-indigo-500 hover:text-red-600 ml-0.5"
                        >×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {repQuery.trim().length < 2 && (
                <p
                  className="text-xs italic text-slate-400 px-3 py-2"
                  data-testid="employee-search-hint"
                >
                  Type at least 2 characters to search employees...
                </p>
              )}
              {searching && (
                <p className="text-xs italic text-slate-500 px-3 py-2 flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Searching...
                </p>
              )}
              {!searching && repQuery.trim().length >= 2 && reps.length === 0 && (
                <p
                  className="text-xs italic text-slate-400 px-3 py-2"
                  data-testid="employee-search-empty"
                >
                  No employees match "{repQuery}".
                </p>
              )}
              {!searching && reps.map((r) => (
                <label
                  key={r.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                  data-testid={`bulk-assign-rep-${r.id}`}
                  data-employee-search-result={r.id}
                >
                  <input
                    type="checkbox"
                    checked={targetIds.includes(r.id)}
                    onChange={() => toggleTarget(r)}
                    data-testid={`bulk-assign-rep-checkbox-${r.id}`}
                  />
                  <span className="font-medium text-slate-800" data-testid={`employee-search-result-${r.id}`}>
                    {r.full_name || r.email}
                  </span>
                  <span className="text-xs text-slate-500">{r.email}</span>
                  {r.company_name && (
                    <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-400 whitespace-nowrap">
                      {r.company_name}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>

          {targetIds.length > 1 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Distribution mode</p>
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="bulk-assign-mode-rr">
                  <input type="radio" name="mode" value="round_robin"
                    checked={mode === 'round_robin'} onChange={() => setMode('round_robin')} />
                  Round robin (even split)
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="bulk-assign-mode-single">
                  <input type="radio" name="mode" value="single"
                    checked={mode === 'single'} onChange={() => setMode('single')} />
                  Single rep (all to first)
                </label>
              </div>
            </div>
          )}

          {preview && (
            <div className="border rounded p-3 bg-emerald-50/50" data-testid="bulk-assign-preview-result">
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Preview</p>
              <p className="text-sm text-slate-800">
                Will assign <strong>{preview.will_assign}</strong> of <strong>{preview.matched_total}</strong> matching leads
                in <em>{preview.mode}</em> mode.
              </p>
              <ul className="mt-2 text-sm grid grid-cols-2 gap-y-1">
                {preview.distribution.map((d) => (
                  <li key={d.rep_id} className="flex items-center justify-between gap-3 pr-3">
                    <span className="text-slate-700">{d.rep_name}</span>
                    <span className="font-mono text-slate-900">{d.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="bulk-assign-cancel">Cancel</Button>
          <Button variant="outline" onClick={runPreview} disabled={busy || !targetIds.length} data-testid="bulk-assign-preview-btn">
            {busy && !preview ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Filter className="h-4 w-4 mr-1" />Preview</>}
          </Button>
          <Button
            onClick={runAssign}
            disabled={busy || !preview || !preview.will_assign}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="bulk-assign-apply-btn"
          >
            {busy && preview ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" />Apply</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
export default function LeadWarehousePage() {
  const [counts, setCounts]           = useState(null);
  const [leads, setLeads]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [filters, setFilters]         = useState({
    state: '', zip_code: '', lead_source: '', import_batch_id: '',
    source: '', lead_status: '', assigned: '',
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [reps, setReps]               = useState([]);
  const [batches, setBatches]         = useState([]);
  const [assignOpen, setAssignOpen]   = useState(false);

  const loadCounts   = () => api.get('/lead-warehouse/counts').then(({ data }) => setCounts(data));
  const loadBatches  = () => api.get('/lead-warehouse/import-batches').then(({ data }) => setBatches(data.items || []));
  // v3.20-1508a WAREHOUSE-ASSIGN-FIX — Target Reps for Bulk Assign now
  // returns EVERY active user whose email ends with
  // `@prestigioustravelclub.org`, regardless of role, company,
  // permission group, or rep_status. See
  // /api/lead-warehouse/assignable-reps for the enforced business rule.
  const loadReps     = () => api.get('/lead-warehouse/assignable-reps').then(({ data }) => {
    const arr = Array.isArray(data) ? data : (data.items || data.reps || data.users || []);
    setReps(arr.map((u) => ({ id: u.id || u.user_id, full_name: u.full_name, email: u.email })));
  });

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v != null && v !== '')
      );
      const { data } = await api.get('/lead-warehouse/leads', { params: { ...params, limit: 500 } });
      setLeads(data.items || []);
      setSelectedIds([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadCounts(); loadBatches(); loadReps(); loadLeads(); }, []);

  const onApplyFilters = () => loadLeads();
  const onClearFilters = () => {
    setFilters({ state: '', zip_code: '', lead_source: '', import_batch_id: '', source: '', lead_status: '', assigned: '' });
    setTimeout(loadLeads, 0);
  };

  const allSelected = leads.length > 0 && selectedIds.length === leads.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : leads.map((l) => l.id));
  const toggleOne = (id) =>
    setSelectedIds((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const onAssignSuccess = () => { loadCounts(); loadLeads(); };

  const returnToWarehouse = async (lead) => {
    if (!window.confirm(`Return "${lead.full_name || lead.id}" to LEAD WAREHOUSE? This clears ownership and resets pressure.`)) return;
    try {
      await api.post(`/lead-warehouse/clients/${lead.id}/return-to-warehouse`);
      toast.success('Returned to warehouse');
      loadCounts(); loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to return to warehouse');
    }
  };

  const selectorFromFilters = useMemo(() => ({
    state:           filters.state || undefined,
    zip_code:        filters.zip_code || undefined,
    lead_source:     filters.lead_source || undefined,
    import_batch_id: filters.import_batch_id || undefined,
    source:          filters.source || undefined,
    only_unassigned: filters.assigned !== 'yes',
  }), [filters]);

  return (
    <div className="space-y-4" data-testid="lead-warehouse-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl text-slate-900">Lead Warehouse</h1>
          <p className="text-sm text-slate-500 mt-1">
            Imported leads with the Pressure System OFF. Distribute deliberately — assignment does not start pressure; the first outbound contact does.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { loadCounts(); loadLeads(); }} disabled={loading} data-testid="lw-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          <Button
            onClick={() => setAssignOpen(true)}
            disabled={!leads.length}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="lw-bulk-assign-btn"
          >
            <UsersIcon className="h-4 w-4 mr-1" /> Bulk Assign
            {selectedIds.length > 0 && <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded text-[11px]">{selectedIds.length}</span>}
          </Button>
        </div>
      </div>

      {/* Counters */}
      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2" data-testid="lw-counters">
          <div className="border rounded px-3 py-2 bg-slate-50">
            <div className="text-[10px] uppercase text-slate-500">Total</div>
            <div className="text-lg font-semibold text-slate-800" data-testid="lw-count-total">{counts.total}</div>
          </div>
          {['Imported','Assigned','Working','Qualified','Application Started'].map((s) => (
            <div key={s} className="border rounded px-3 py-2">
              <div className="text-[10px] uppercase text-slate-500 truncate">{s}</div>
              <div className="text-lg font-semibold text-slate-800" data-testid={`lw-count-${s}`}>{counts.by_status?.[s] || 0}</div>
            </div>
          ))}
          <div className="border rounded px-3 py-2 bg-amber-50">
            <div className="text-[10px] uppercase text-slate-500">Pressure ON</div>
            <div className="text-lg font-semibold text-slate-800" data-testid="lw-count-pressure-on">{counts.by_status?.pressure_on || 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="border rounded p-3 grid grid-cols-2 md:grid-cols-7 gap-2 items-end" data-testid="lw-filter-bar">
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">State</Label>
          <Input value={filters.state} onChange={(e) => setFilters((f) => ({...f, state: e.target.value}))}
                 className="h-9 text-sm" placeholder="FL" data-testid="lw-filter-state" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">ZIP</Label>
          <Input value={filters.zip_code} onChange={(e) => setFilters((f) => ({...f, zip_code: e.target.value}))}
                 className="h-9 text-sm" placeholder="33101" data-testid="lw-filter-zip" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Lead Source</Label>
          <Input value={filters.lead_source} onChange={(e) => setFilters((f) => ({...f, lead_source: e.target.value}))}
                 className="h-9 text-sm" placeholder="Experian" data-testid="lw-filter-lead-source" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Import Batch</Label>
          <Select value={filters.import_batch_id || 'all'} onValueChange={(v) => setFilters((f) => ({...f, import_batch_id: v === 'all' ? '' : v}))}>
            <SelectTrigger className="h-9 text-sm" data-testid="lw-filter-batch"><SelectValue placeholder="All batches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All batches</SelectItem>
              {batches.map((b) => (
                <SelectItem key={b.import_batch_id} value={b.import_batch_id}>
                  {b.import_batch_id.slice(0,8)} — {b.count} ({fmtDate(b.imported_at)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Status</Label>
          <Select value={filters.lead_status || 'all'} onValueChange={(v) => setFilters((f) => ({...f, lead_status: v === 'all' ? '' : v}))}>
            <SelectTrigger className="h-9 text-sm" data-testid="lw-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {['Imported','Assigned','Working','Qualified','Application Started'].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Assigned?</Label>
          <Select value={filters.assigned || 'all'} onValueChange={(v) => setFilters((f) => ({...f, assigned: v === 'all' ? '' : v}))}>
            <SelectTrigger className="h-9 text-sm" data-testid="lw-filter-assigned"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="no">Unassigned only</SelectItem>
              <SelectItem value="yes">Assigned only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClearFilters} size="sm" className="h-9" data-testid="lw-filter-clear">Clear</Button>
          <Button onClick={onApplyFilters} size="sm" className="h-9 bg-slate-900 text-white hover:bg-slate-800" data-testid="lw-filter-apply">Apply</Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="lw-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 w-8">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="lw-row-checkbox-all" />
              </th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Lead</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Phone / Email</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">State / ZIP</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Source / Batch</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Status</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Owner</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400 italic">No warehouse leads match your filters.</td></tr>
            )}
            {!loading && leads.map((l) => (
              <tr key={l.id} className="border-b last:border-0 hover:bg-indigo-50/30" data-testid={`lw-row-${l.id}`}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(l.id)}
                    onChange={() => toggleOne(l.id)}
                    data-testid={`lw-row-checkbox-${l.id}`}
                  />
                </td>
                <td className="px-3 py-2 text-slate-800 font-medium">{l.full_name || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-slate-700 text-xs">
                  <div>{l.phone || '—'}</div>
                  <div className="text-slate-500">{l.email || '—'}</div>
                </td>
                <td className="px-3 py-2 text-slate-700 text-xs">{l.state || '—'} {l.zip_code || ''}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">
                  <div>{l.lead_source || '—'}</div>
                  <div className="text-slate-400 font-mono">{l.import_batch_id ? l.import_batch_id.slice(0,8) : '—'}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex text-[11px] font-medium px-2 py-0.5 rounded ${STATUS_TONES[l.lead_status] || 'bg-slate-100 text-slate-700'}`}>
                    {l.lead_status || '—'}
                  </span>
                  {l.pressure_enabled && <CheckCircle2 className="h-3 w-3 inline-block ml-1 text-amber-600" title="Pressure ON" />}
                </td>
                <td className="px-3 py-2 text-slate-700 text-xs" data-testid={`lw-row-owner-${l.id}`}>
                  {l.assigned_to_name || (l.assigned_to ? <span className="font-mono">{l.assigned_to.slice(0,8)}</span> : <span className="text-slate-300">—</span>)}
                </td>
                <td className="px-3 py-2">
                  {/* v3.21-1685 · Row-scoped assign/return controls with
                      stable data-testids so DOM automation can drive the
                      same interface a real operator uses.  "Assign"
                      opens the Bulk Assign modal with this one lead
                      pre-selected — same authorization/backend path. */}
                  <button
                    type="button"
                    onClick={() => { setSelectedIds([l.id]); setAssignOpen(true); }}
                    className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 mr-3"
                    data-testid={`lw-assign-${l.id}`}
                    title="Assign this lead"
                  >
                    <UsersIcon className="h-3 w-3" /> Assign
                  </button>
                  {(l.assigned_to || l.lead_status !== 'Imported') && (
                    <button
                      onClick={() => returnToWarehouse(l)}
                      className="text-xs text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                      data-testid={`lw-return-${l.id}`}
                    >
                      <Undo2 className="h-3 w-3" /> Return
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BulkAssignModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        selector={selectorFromFilters}
        selectedIds={selectedIds}
        onSuccess={onAssignSuccess}
      />
    </div>
  );
}
